/**
 * Buscador de filiais com mapa interativo.
 * Dados vêm de metaobjects via JSON embutido na section.
 */
class FiliaisFinder extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.i18n = this.readJson('[data-i18n]', {});
    this.data = this.normalizeData(this.readJson('[data-filiais]', []));

    this.stateSelect = this.querySelector('[data-state-select]');
    this.cardsContainer = this.querySelector('[data-cards]');
    this.mapEl = this.querySelector('[data-map-interactive]');
    this.statePaths = this.querySelectorAll('.state-path[data-state]');
    this.svgMapEl = this.querySelector('[data-map-svg]');
    this.svgStates = [];
    this.hoverPath = null;

    this.bindEvents();
    requestAnimationFrame(() => this.initHitZones());
    this.loadSvgMap();
    this.applyUrlParams();
  }

  // Mapa mobile: SVG com os estados como <path> de verdade — carregado sob
  // demanda (fetch) pra não pesar no HTML da página em telas maiores, onde
  // ele fica escondido via CSS.
  loadSvgMap() {
    if (!this.svgMapEl) return;
    const url = this.svgMapEl.dataset.svgUrl;
    if (!url) return;

    fetch(url)
      .then((response) => response.text())
      .then((markup) => {
        // innerHTML com uma string crua às vezes não entra em "modo SVG" no
        // parser (o navegador trata <style>/<title>/<text> como texto solto
        // em vez de desenhar o mapa). DOMParser força o parse como SVG de
        // verdade, com o namespace correto.
        const svgEl = new DOMParser().parseFromString(markup, 'image/svg+xml').documentElement;
        if (!svgEl || svgEl.nodeName.toLowerCase() !== 'svg') return;

        this.svgMapEl.innerHTML = '';
        this.svgMapEl.appendChild(document.importNode(svgEl, true));
        this.svgStates = Array.from(this.svgMapEl.querySelectorAll('.estado[data-uf]'));
        this.bindSvgEvents();
        this.updateMapHighlight();
      })
      .catch(() => {
        // Falhou o fetch (ex.: offline) — mapa mobile simplesmente não aparece,
        // os selects continuam funcionando normalmente.
      });
  }

  bindSvgEvents() {
    this.svgStates.forEach((state) => {
      const uf = state.dataset.uf;
      state.addEventListener('mouseenter', () => state.classList.add('is-hover'));
      state.addEventListener('mouseleave', () => state.classList.remove('is-hover'));
      state.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectState(uf);
      });
    });
  }

  readJson(selector, fallback) {
    const el = this.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return fallback;
    }
  }

  comparePt(a, b) {
    return String(a || '').localeCompare(String(b || ''), 'pt', { sensitivity: 'base' });
  }

  sortBranches(entries) {
    return [...entries].sort((a, b) => {
      const byName = this.comparePt(a.name, b.name);
      if (byName !== 0) return byName;
      return this.comparePt(a.coverage, b.coverage);
    });
  }

  normalizeData(raw) {
    const grouped = {};

    (Array.isArray(raw) ? raw : []).forEach((item) => {
      const state = item.state;
      if (!state) return;
      if (!grouped[state]) grouped[state] = [];
      grouped[state].push({
        name: item.name,
        city: item.city,
        coverage: item.coverage,
        phone: item.phone,
        email: item.email,
        website: item.website,
      });
    });

    Object.keys(grouped).forEach((state) => {
      grouped[state] = this.sortBranches(grouped[state]);
    });

    return grouped;
  }

  initHitZones() {
    if (!this.mapEl || this.mapEl.querySelector('.df-mapa__hits')) return;

    const mapW = this.mapEl.clientWidth;
    const mapH = this.mapEl.clientHeight;
    if (!mapW || !mapH) return;

    const layer = document.createElement('div');
    layer.className = 'df-mapa__hits';

    this.statePaths.forEach((path) => {
      const area = path.offsetWidth * path.offsetHeight;
      const size = area < 1800 ? 46 : area < 4500 ? 38 : 30;
      const hit = document.createElement('button');
      hit.type = 'button';
      hit.className = 'df-mapa__hit';
      hit.dataset.state = path.dataset.state;
      hit.setAttribute('aria-label', path.getAttribute('title') || path.dataset.state);
      hit.style.width = `${size}px`;
      hit.style.height = `${size}px`;
      hit.style.left = `${((path.offsetLeft + path.offsetWidth * 0.5) / mapW) * 100}%`;
      hit.style.top = `${((path.offsetTop + path.offsetHeight * 0.55) / mapH) * 100}%`;
      layer.appendChild(hit);
    });

    this.mapEl.appendChild(layer);

    if (!this.resizeObserver && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.mapEl.querySelector('.df-mapa__hits')?.remove();
        this.initHitZones();
      });
      this.resizeObserver.observe(this.mapEl);
    }
  }

  resolveStatePathAt(x, y) {
    const stack = document.elementsFromPoint(x, y);
    const hit = stack.find((el) => el.closest('.df-mapa__hit'));
    if (hit) {
      const code = hit.closest('.df-mapa__hit').dataset.state;
      return this.querySelector(`.state-path[data-state="${CSS.escape(code)}"]`);
    }

    const paths = stack
      .map((el) => el.closest('.state-path[data-state]'))
      .filter(Boolean)
      .filter((el, index, arr) => arr.indexOf(el) === index);

    if (!paths.length) return null;

    return paths.sort(
      (a, b) => a.offsetWidth * a.offsetHeight - b.offsetWidth * b.offsetHeight
    )[0];
  }

  setHoverPath(path) {
    if (this.hoverPath === path) return;
    this.hoverPath?.classList.remove('is-hover');
    this.hoverPath = path;
    path?.classList.add('is-hover');
  }

  selectState(code) {
    if (!code) return;
    this.stateSelect.value = code;
    this.updateMapHighlight();
    this.renderCards();
    this.pushUrl();
  }

  handleMapPointer(x, y) {
    const path = this.resolveStatePathAt(x, y);
    this.setHoverPath(path);
    return path;
  }

  applyUrlParams() {
    const state = new URLSearchParams(window.location.search).get('estado');
    if (state) {
      const option = this.stateSelect.querySelector(`option[value="${CSS.escape(state)}"]`);
      if (option) this.stateSelect.value = state;
    }
    this.updateMapHighlight();
    this.renderCards();
  }

  pushUrl() {
    const params = new URLSearchParams();
    const state = this.stateSelect.value;
    if (state) params.set('estado', state);
    const query = params.toString();
    window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }

  bindEvents() {
    this.stateSelect.addEventListener('change', () => {
      this.updateMapHighlight();
      this.renderCards();
      this.pushUrl();
    });

    if (!this.mapEl) return;

    this.mapEl.addEventListener('mousemove', (event) => {
      this.handleMapPointer(event.clientX, event.clientY);
    });

    this.mapEl.addEventListener('mouseleave', () => this.setHoverPath(null));

    this.mapEl.addEventListener('click', (event) => {
      const path = this.handleMapPointer(event.clientX, event.clientY);
      if (!path) return;
      event.preventDefault();
      this.selectState(path.dataset.state);
    });

    this.mapEl.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const path = this.resolveStatePathAt(touch.clientX, touch.clientY);
      if (!path) return;
      event.preventDefault();
      this.selectState(path.dataset.state);
    });

    this.statePaths.forEach((path) => {
      path.addEventListener('click', (event) => event.preventDefault());
    });
  }

  updateMapHighlight() {
    const state = this.stateSelect.value;

    this.statePaths.forEach((path) => path.classList.remove('is-active'));
    this.svgStates.forEach((el) => el.classList.remove('is-active'));

    if (!state) return;

    const path = this.querySelector(`.state-path[data-state="${CSS.escape(state)}"]`);
    path?.classList.add('is-active');

    const svgState = this.svgStates.find((el) => el.dataset.uf === state);
    svgState?.classList.add('is-active');
  }

  renderCards() {
    const state = this.stateSelect.value;
    this.cardsContainer.innerHTML = '';
    if (!state) return;

    const branches = this.data[state] || [];
    if (!branches.length) {
      this.cardsContainer.innerHTML = `<p class="distributor-finder__empty">${this.escape(
        this.i18n.empty || 'Nenhuma filial encontrada para este estado.'
      )}</p>`;
      return;
    }

    this.sortBranches(branches).forEach((branch, index) => {
      const card = document.createElement('div');
      card.className = 'distributor-card';
      card.style.animationDelay = `${index * 0.05}s`;
      card.innerHTML = `
        <div class="distributor-card__info">
          <p class="distributor-card__name">${this.escape(branch.name)}</p>
          ${branch.coverage ? `<p class="distributor-card__city">${this.escape(branch.coverage)}</p>` : ''}
          ${
            branch.city && branch.city !== branch.coverage
              ? `<div class="distributor-card__detail">${this.iconLocation()}<span>${this.escape(branch.city)}</span></div>`
              : ''
          }
          ${
            branch.phone
              ? `<div class="distributor-card__detail">${this.iconPhone()}<a href="tel:${this.escape(branch.phone)}">${this.escape(branch.phone)}</a></div>`
              : ''
          }
          ${
            branch.email
              ? `<div class="distributor-card__detail">${this.iconEmail()}<a href="mailto:${this.escape(branch.email)}">${this.escape(branch.email)}</a></div>`
              : ''
          }
          ${
            branch.website
              ? `<div class="distributor-card__detail">${this.iconWeb()}<a href="${this.buildUrl(branch.website)}" target="_blank" rel="noopener">${this.escape(branch.website)}</a></div>`
              : ''
          }
        </div>`;
      this.cardsContainer.appendChild(card);
    });
  }

  buildUrl(site) {
    if (!site) return '#';
    return site.startsWith('http') ? site : `https://${site}`;
  }

  escape(value) {
    if (!value) return '';
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  iconLocation() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
  }

  iconPhone() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
  }

  iconEmail() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>';
  }

  iconWeb() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';
  }
}

customElements.define('filiais-finder', FiliaisFinder);
